import React from 'react';

import dayjs from '~/timeInit';

import {
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import TenantBrandChip from '~/components/Common/Elements/TenantBrandChip';

import useAppNavigation from '~/hooks/useAppNavigation';

import { getUserTenant } from '~/utils/userIdentity';

import OrderItem from './OrderItem';

interface Props {
  user: App.User;
  orders: Array<App.Order>;
}

export default function UserCard({ user, orders }: Props) {
  const { push } = useAppNavigation();
  const tenant = getUserTenant(user);

  return (
    <Box sx={{ mb: 2, mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="h6" gutterBottom>
        <Link
          href={`/users/${user.id_member}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textDecoration: 'none', color: 'primary.main' }}
        >
          {user.fullName}
        </Link>
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {user.email}
          {tenant && <TenantBrandChip tenantValue={tenant} />}
        </Box>
        {user.lastLogin?.login_date && <span> • Last login {dayjs(user.lastLogin.login_date).fromNow()}</span>} •{' '}
        <Link
          href={`/purchases/section/allOrders?customer_id=${user.id_member}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'secondary.main', textDecoration: 'underline' }}
        >
          All Purchases ({orders.length})
        </Link>
      </Typography>

      {orders.length > 0 && (
        <Box>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small" aria-label="orders table">
              <TableHead>
                <TableRow>
                  <TableCell>Purchased</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell>View Order</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => {
                  const isCompleted = order.status === 'completed';
                  return (
                    <TableRow
                      key={order.id}
                      hover
                      onClick={() => push(`/purchases/${order.id}`)}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                        ...(isCompleted
                          ? {}
                          : {
                              opacity: 0.6,
                            }),
                      }}
                    >
                      <OrderItem order={order} />
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
